import { useCallback, useState } from "react"
import { apiFetch } from "../../../utils/apiFetch"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

export default function useDiagnosisInitialModal({
  clinicId,
  patientId,
  isReadOnly,
  readOnlyReason,
  getInitialForm,
  mapInstanceToForm,
  onSaved,
  onSendToTreatmentPlan,
}) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(() => getInitialForm())
  const [latestInstance, setLatestInstance] = useState(null)

  const resetFormState = useCallback(() => {
    setLatestInstance(null)
    setForm(getInitialForm())
  }, [getInitialForm])

  const loadLatestInstance = useCallback(async () => {
    try {
      if (!patientId || !clinicId) {
        resetFormState()
        return null
      }

      setLoading(true)

      const params = new URLSearchParams({
        patientId,
        clinicId,
        templateCode: "INITIAL_DIAGNOSIS",
      })

      const res = await apiFetch(
        `${API_BASE}/clinical-history/form-instances?${params.toString()}`
      )
      const data = await res.json().catch(() => [])

      if (!res.ok) {
        resetFormState()
        return null
      }

      const rows = Array.isArray(data) ? data : []
      const latest = rows.length ? rows[0] : null

      setLatestInstance(latest)
      setForm(mapInstanceToForm(latest))
      return latest
    } catch {
      resetFormState()
      return null
    } finally {
      setLoading(false)
    }
  }, [patientId, clinicId, mapInstanceToForm, resetFormState])

  const openModal = useCallback(async () => {
    await loadLatestInstance()
    setOpen(true)
  }, [loadLatestInstance])

  const closeModal = useCallback(() => {
    setOpen(false)
  }, [])

  const saveInstance = useCallback(async () => {
    try {
      if (isReadOnly) {
        alert(readOnlyReason)
        return null
      }

      if (!patientId || !clinicId) {
        alert("Falta contexto de paciente o clÃ­nica.")
        return null
      }

      if (!form.mainDiagnosis.trim()) {
        alert("Escribe el diagnÃ³stico principal.")
        return null
      }

      setSaving(true)

      const summaryParts = [
        form.mainDiagnosis || "",
        form.tooth ? `Pieza ${form.tooth}` : "",
        form.priority ? `Prioridad ${form.priority}` : "",
      ].filter(Boolean)

      const res = await apiFetch(`${API_BASE}/clinical-history/form-instances`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          clinicId,
          patientId,
          templateCode: "INITIAL_DIAGNOSIS",
          formInstanceId: latestInstance?.id || undefined,
          source: "DOCTOR",
          status: "DRAFT",
          title: `DiagnÃ³stico inicial${form.tooth ? ` Â· Pieza ${form.tooth}` : ""}`,
          summary: summaryParts.join(" Â· "),
          dataJson: form,
        }),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo guardar el diagnÃ³stico inicial.")
        return null
      }

      setLatestInstance(data || null)

      if (typeof onSaved === "function") {
        await onSaved(data || null, form)
      }

      alert("DiagnÃ³stico inicial guardado correctamente.")

      if (form.sendToTreatmentPlan) {
        setOpen(false)

        if (typeof onSendToTreatmentPlan === "function") {
          onSendToTreatmentPlan(data || null, form)
        }

        return data || null
      }

      setOpen(false)
      return data || null
    } catch {
      alert("Error al guardar el diagnÃ³stico inicial.")
      return null
    } finally {
      setSaving(false)
    }
  }, [
    isReadOnly,
    readOnlyReason,
    patientId,
    clinicId,
    form,
    latestInstance,
    onSaved,
    onSendToTreatmentPlan,
  ])

  return {
    open,
    setOpen,
    loading,
    saving,
    form,
    setForm,
    latestInstance,
    setLatestInstance,
    loadLatestInstance,
    openModal,
    closeModal,
    saveInstance,
  }
}
